import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { toast } from 'sonner';
import { Stethoscope, Lock, Eye, EyeOff, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [validating, setValidating] = useState(true);
  const [tokenError, setTokenError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-password/${token}`)
      .then(res => { setEmail(res.data.email); })
      .catch(e => setTokenError(e.response?.data?.detail || 'Invalid or expired reset link.'))
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/auth/reset-password/${token}`, { password });
      toast.success(res.data.message);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-amber-50 flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-amber-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <Stethoscope className="w-7 h-7 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl text-slate-800">Guatemala Surgery</span>
          </Link>
        </div>

        <Card className="border-0 shadow-xl rounded-2xl">
          <CardHeader className="text-center pt-8 pb-2">
            {tokenError ? (
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertCircle className="w-7 h-7 text-red-500" />
              </div>
            ) : done ? (
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
            ) : (
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Lock className="w-7 h-7 text-primary" />
              </div>
            )}
            <CardTitle className="text-2xl font-heading font-bold text-slate-800">
              {tokenError ? 'Link Invalid' : done ? 'Password Reset!' : 'Set New Password'}
            </CardTitle>
            {!tokenError && !done && email && (
              <CardDescription className="mt-1">for <strong>{email}</strong></CardDescription>
            )}
          </CardHeader>

          <CardContent className="p-8 pt-4">
            {tokenError ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-slate-500">{tokenError}</p>
                <Link to="/forgot-password">
                  <Button className="w-full rounded-xl bg-primary">Request a New Reset Link</Button>
                </Link>
              </div>
            ) : done ? (
              <div className="text-center space-y-3">
                <p className="text-sm text-slate-500">Your password has been updated. Redirecting to sign in...</p>
                <Link to="/login">
                  <Button className="w-full rounded-xl bg-primary">Go to Sign In</Button>
                </Link>
              </div>
            ) : (
              <form noValidate onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="New password (min. 6 characters)"
                    className="pl-10 pr-10 h-12 rounded-xl"
                    required
                    autoFocus
                    data-testid="new-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="pl-10 h-12 rounded-xl"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button type="submit" className="w-full h-12 rounded-xl bg-primary" disabled={submitting} data-testid="reset-submit-btn">
                  {submitting ? 'Updating...' : 'Set New Password'}
                  {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
                <p className="text-center text-sm text-slate-500">
                  <Link to="/login" className="text-primary hover:underline">← Back to Sign In</Link>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
